/**
 * Custom hook for Contagem navigation management
 *
 * Manages:
 * - Navigation between jobs (previous/next)
 * - Available job IDs list
 * - URL parameter handling for tabs
 * - Back navigation with state preservation
 *
 * Usage:
 * const { hasPrevious, hasNext, handlePrevious, handleNext, ... } = useContagemNavigation(id);
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import jobService from '../services/jobService';

export interface UseContagemNavigationReturn {
  // State
  availableJobIds: number[];
  activeTab: string;
  isNewContagem: boolean;
  currentJobId: number | null;

  // Navigation computed
  hasPrevious: boolean;
  hasNext: boolean;
  currentIndex: number;

  // URL params
  dateParam: string | null;
  fromParam: string | null;

  // Actions
  handlePrevious: () => void;
  handleNext: () => void;
  handleNavigateToJob: (jobId: number) => void;
  handleVoltar: () => void;
  setActiveTab: (tab: string) => void;
}

export const useContagemNavigation = (id: string | undefined): UseContagemNavigationReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [availableJobIds, setAvailableJobIds] = useState<number[]>([]);

  const isNewContagem = id === 'new';
  const dateParam = searchParams.get('date');
  const fromParam = searchParams.get('from');
  const activeTab = searchParams.get('tab') || 'agendamento';

  // Current job navigation
  const currentJobId = id && id !== 'new' ? parseInt(id) : null;
  const currentIndex = currentJobId ? availableJobIds.indexOf(currentJobId) : -1;
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < availableJobIds.length - 1;

  // Load available job IDs for navigation
  useEffect(() => {
    const loadAvailableJobIds = async () => {
      try {
        const firstPage = await jobService.getJobs({ page: 1, page_size: 100 });
        let allJobs = [...firstPage.items];

        if (firstPage.total_pages > 1) {
          const remainingPages = Array.from(
            { length: firstPage.total_pages - 1 },
            (_, i) => i + 2
          );

          const otherPages = await Promise.all(
            remainingPages.map(page =>
              jobService.getJobs({ page, page_size: 100 })
            )
          );

          otherPages.forEach(response => {
            allJobs = [...allJobs, ...response.items];
          });
        }

        const jobIds = allJobs.map(job => job.id).sort((a, b) => a - b);
        setAvailableJobIds(jobIds);
      } catch (error) {
        console.error('Erro ao carregar IDs disponíveis:', error);
      }
    };

    if (!isNewContagem) {
      loadAvailableJobIds();
    }
  }, [isNewContagem]);

  const handleNavigateToJob = useCallback((newJobId: number) => {
    const currentTab = searchParams.get('tab');
    let newUrl = `/cloud/contagens/${newJobId}`;

    const params = new URLSearchParams();
    if (currentTab) params.set('tab', currentTab);
    if (fromParam) params.set('from', fromParam);
    if (dateParam) params.set('date', dateParam);

    if (params.toString()) {
      newUrl += `?${params.toString()}`;
    }

    navigate(newUrl);
  }, [searchParams, fromParam, dateParam, navigate]);

  const handlePrevious = useCallback(() => {
    if (hasPrevious) {
      const previousJobId = availableJobIds[currentIndex - 1];
      handleNavigateToJob(previousJobId);
    }
  }, [hasPrevious, availableJobIds, currentIndex, handleNavigateToJob]);

  const handleNext = useCallback(() => {
    if (hasNext) {
      const nextJobId = availableJobIds[currentIndex + 1];
      handleNavigateToJob(nextJobId);
    }
  }, [hasNext, availableJobIds, currentIndex, handleNavigateToJob]);

  const handleVoltar = useCallback(() => {
    const state = location.state as { from?: string; selectedDate?: string };

    if (state?.from === '/cloud/calendario' && state?.selectedDate) {
      navigate('/cloud/calendario', {
        state: { selectedDate: state.selectedDate }
      });
    } else if (window.history.state?.idx > 0) {
      navigate(-1);
    } else {
      navigate('/cloud/contagens');
    }
  }, [location.state, navigate]);

  const setActiveTab = useCallback((tab: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  return {
    // State
    availableJobIds,
    activeTab,
    isNewContagem,
    currentJobId,

    // Navigation computed
    hasPrevious,
    hasNext,
    currentIndex,

    // URL params
    dateParam,
    fromParam,

    // Actions
    handlePrevious,
    handleNext,
    handleNavigateToJob,
    handleVoltar,
    setActiveTab,
  };
};

export default useContagemNavigation;
